import { Alert, Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { MouseEvent } from 'react';
import useLocalization from '../../hooks/useLocalization';
import KycVerificationPanel from '../molecules/KycVerificationPanel';
import PaymentProviders from '../atoms/PaymentProviders';
import BalanceContact from '../atoms/BalanceContact';
import CloseIcon from '@mui/icons-material/Close';
import { PAYMENT_TRANSACTION_TYPE, type PaymentTransactionType } from '../../types/enums';
import { usePaymentFlowState } from '../../hooks/payment/usePaymentFlowState';
import { useWithdrawalBonusGuard } from '../../hooks/payment/useWithdrawalBonusGuard';
import { isActiveWelcomeBonus } from '../../queries/bonus';
import useKycAttempts from '../../hooks/useKycAttempts';
import { useStartKycVerification, useUserData } from '../../queries';
import { useMarketplaceConfig } from '../../queries/marketplace';
import ForfeitBonusDialog from '../molecules/ForfeitBonusDialog';
import PaymentFormRenderer from './PaymentFormRenderer';
import { FormattedBalance } from '../atoms/FormattedBalance';
import { shouldShowTransactionKyc } from '../../utils/kyc';
import WelcomeBonusSelectionGate from '../molecules/WelcomeBonusSelectionGate';
import BonusMinimumDepositNotice from '../molecules/BonusMinimumDepositNotice';

const styles = {
  rootContainer: {
    p: 3,
    maxHeight: 'inherit',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'neutral.25',
    pb: 1,
  },
  headerButtonContainer: {
    flex: 1,
  },
  headerButtonContainerRight: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  icon: {
    color: 'neutral.25',
    fontSize: { xs: '1rem', md: '1.5rem' },
  },
  closeButton: {
    display: { xs: 'none', md: 'flex' },
  },
  contentStack: {
    mt: { xs: 2, md: 3 },
    gap: 2,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    gap: 2,
    overflowY: 'auto',
  },
  toggleButton: {
    width: '50%',
    height: 40,
    backgroundColor: 'neutral.600',
    color: 'neutral.25',
    borderRadius: 2,
    '&.Mui-selected': {
      color: 'primary.contrastText',
      backgroundColor: 'primary.main',
      '&:hover': {
        backgroundColor: 'primary.main',
      },
    },
  },
};

const BalanceModal = ({
  handleClose,
  showBalanceContact,
}: {
  handleClose: () => void;
  showBalanceContact: boolean;
}) => {
  const { t } = useLocalization();
  const { data: userData } = useUserData();
  const { data: marketplaceConfig } = useMarketplaceConfig();
  const {
    transactionType,
    resolvedPaymentProvider,
    paymentGateway,
    paymentProviders,
    isDeposit,
    isWithdrawal,
    cryptoCashier,
    showPaymentProviderPicker,
    canShowPaymentForm,
    withdrawalDisabled,
    withdrawalDisabledReason,
    handleTransactionTypeChange,
    handleProviderChange,
    resetToDeposit,
  } = usePaymentFlowState();

  const {
    firstDepositBonus,
    isForfeitDialogOpen,
    shouldBlockWithdrawal,
    setWithdrawalBonusCleared,
    closeForfeitDialog,
    handleForfeitCancel,
  } = useWithdrawalBonusGuard({ isWithdrawal, resetToDeposit });
  const { mutate: startVerification, isPending: isVerificationPending } = useStartKycVerification();
  const { hasReachedLimit } = useKycAttempts(userData?.kycAttempts);
  const marketplaceKycConfig = marketplaceConfig?.marketplaceConfig.kyc;
  const showForm = canShowPaymentForm && !shouldBlockWithdrawal;
  const shouldShowKycGate = shouldShowTransactionKyc(userData, marketplaceKycConfig, transactionType);

  const handleTransactionTypeChangeWrapper = (
    _event: MouseEvent<HTMLElement>,
    newTransactionType: PaymentTransactionType | null
  ) => {
    handleTransactionTypeChange(newTransactionType);
  };

  return (
    <Box sx={styles.rootContainer} data-testid='balance-modal-shell'>
      <Box sx={styles.header}>
        <Box sx={styles.headerButtonContainer} />
        <Typography
          variant='h3'
          sx={{
            textAlign: 'center',
            color: 'neutral.200',
          }}
        >
          {t('balance')}: <FormattedBalance balanceType='main' />
        </Typography>
        <Box sx={styles.headerButtonContainerRight}>
          <IconButton aria-label={t('close')} onClick={handleClose} sx={styles.closeButton}>
            <CloseIcon sx={styles.icon} />
          </IconButton>
        </Box>
      </Box>
      <Stack sx={styles.contentStack}>
        <ToggleButtonGroup
          color='primary'
          value={transactionType}
          exclusive
          onChange={handleTransactionTypeChangeWrapper}
          aria-label='Platform'
        >
          <ToggleButton sx={styles.toggleButton} value={PAYMENT_TRANSACTION_TYPE.DEPOSIT}>
            {t('deposit')}
          </ToggleButton>
          <ToggleButton
            sx={styles.toggleButton}
            value={PAYMENT_TRANSACTION_TYPE.WITHDRAW}
            disabled={withdrawalDisabled}
          >
            {t('withdraw')}
          </ToggleButton>
        </ToggleButtonGroup>
        {withdrawalDisabledReason === 'provider_unavailable' && (
          <Alert severity='info'>{t('zeroXWithdrawalUnavailable')}</Alert>
        )}
        <Stack sx={styles.scrollArea} data-testid='balance-modal-scroll-area'>
          <WelcomeBonusSelectionGate enabled={isDeposit}>
            {shouldShowKycGate ? (
              <KycVerificationPanel
                attemptsExceeded={hasReachedLimit}
                isPending={isVerificationPending}
                onStartVerification={() => startVerification()}
                status={userData?.kycStatus}
                trigger={marketplaceKycConfig?.trigger}
              />
            ) : (
              <>
                {isDeposit && <BonusMinimumDepositNotice bonus={firstDepositBonus} />}
                {!shouldBlockWithdrawal && showPaymentProviderPicker && (
                  <PaymentProviders
                    transactionType={transactionType}
                    selectedProvider={resolvedPaymentProvider}
                    onProviderChange={handleProviderChange}
                    paymentProviders={paymentProviders}
                    paymentGateway={paymentGateway}
                  />
                )}
                {!shouldBlockWithdrawal && showBalanceContact && <BalanceContact />}
                {showForm && (
                  <PaymentFormRenderer
                    cryptoCashier={cryptoCashier}
                    paymentGateway={paymentGateway}
                    transactionType={transactionType}
                    paymentProvider={resolvedPaymentProvider}
                  />
                )}
              </>
            )}
          </WelcomeBonusSelectionGate>
        </Stack>
      </Stack>
      {firstDepositBonus && isActiveWelcomeBonus(firstDepositBonus) && (
        <ForfeitBonusDialog
          bonus={firstDepositBonus}
          onCancel={handleForfeitCancel}
          onClose={closeForfeitDialog}
          onSuccess={() => {
            setWithdrawalBonusCleared(true);
            closeForfeitDialog();
          }}
          open={isForfeitDialogOpen}
          productType={firstDepositBonus.campaignProduct}
        />
      )}
    </Box>
  );
};

export default BalanceModal;
